import { useMemo, useState } from "react";
import type { StudioDeploymentDecision } from "../../lib/studio/studioDeploymentState";
import type { StudioImproveLoopReadModel } from "../../lib/studio/studioImproveLoopReadModel";
import type { StudioPremiumDemoModel } from "../../lib/studio/studioPremiumDemo";
import {
  buildStudioExportPackage,
  buildStudioPublishPackage,
  formatStudioExportMarkdownSummary,
  formatStudioPublishMarkdownSummary,
  type StudioExportPackage,
  type StudioPublishPackage,
} from "../../lib/studio/studioExport";
import type { StudioWorkspaceState } from "../../lib/studio/studioWorkspaceState";

type StudioExportView = "json" | "markdown";
type StudioPackageKind = "export" | "publish";

function buildDownloadFileName(
  packageKind: StudioPackageKind,
  view: StudioExportView,
  generatedAt: string
) {
  const timestamp = generatedAt.replace(/[^0-9A-Za-z]+/g, "-").replace(/-+$/g, "");
  return `vetcan-studio-${packageKind}-${timestamp}.${view === "json" ? "json" : "md"}`;
}

function downloadStudioExportContent(params: {
  content: string;
  fileName: string;
  mimeType: string;
}) {
  if (
    typeof document === "undefined" ||
    typeof window === "undefined" ||
    typeof window.URL?.createObjectURL !== "function" ||
    typeof window.URL?.revokeObjectURL !== "function"
  ) {
    return;
  }

  const blob = new Blob([params.content], {
    type: params.mimeType,
  });
  const objectUrl = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");

  anchor.href = objectUrl;
  anchor.download = params.fileName;
  anchor.rel = "noopener";
  anchor.style.display = "none";
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.URL.revokeObjectURL(objectUrl);
}

function summaryCard(label: string, value: string, detail: string) {
  return (
    <div
      style={{
        borderRadius: 20,
        border: "1px solid rgba(54, 44, 35, 0.08)",
        background:
          "linear-gradient(180deg, rgba(251, 248, 243, 0.98), rgba(255, 255, 255, 0.98))",
        padding: "0.95rem",
      }}
    >
      <div
        style={{
          fontSize: "0.72rem",
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: "#85796e",
          fontWeight: 700,
        }}
      >
        {label}
      </div>
      <div
        style={{
          marginTop: "0.38rem",
          color: "#26221d",
          fontWeight: 700,
          fontSize: "1.05rem",
        }}
      >
        {value}
      </div>
      <p style={{ margin: "0.42rem 0 0", color: "#5b534b", lineHeight: 1.55 }}>
        {detail}
      </p>
    </div>
  );
}

function viewButton(
  label: string,
  view: StudioExportView,
  activeView: StudioExportView,
  onSelect: (view: StudioExportView) => void
) {
  const active = view === activeView;

  return (
    <button
      type="button"
      onClick={() => onSelect(view)}
      style={{
        borderRadius: 999,
        padding: "0.72rem 1rem",
        border: active
          ? "1px solid rgba(41, 84, 103, 0.18)"
          : "1px solid rgba(54, 44, 35, 0.12)",
        background: active ? "linear-gradient(135deg, #173847, #285466)" : "#fff9f2",
        color: active ? "#f7f4ee" : "#3e372f",
        fontWeight: 700,
        cursor: "pointer",
      }}
    >
      {label}
    </button>
  );
}

function serializeStudioExportPackage(exportPackage: StudioExportPackage) {
  return JSON.stringify(exportPackage, null, 2);
}

function serializeStudioPublishPackage(publishPackage: StudioPublishPackage) {
  return JSON.stringify(publishPackage, null, 2);
}

export default function ExportPanel(props: {
  workspaceState: StudioWorkspaceState;
  premiumDemo: StudioPremiumDemoModel;
  improveLoopModel: StudioImproveLoopReadModel;
  deploymentDecision?: StudioDeploymentDecision | null;
  generatedAt?: string;
}) {
  const [activeExportView, setActiveExportView] = useState<StudioExportView>("json");
  const [activePublishView, setActivePublishView] = useState<StudioExportView>("json");

  const exportPackage = useMemo(
    () =>
      buildStudioExportPackage({
        generatedAt: props.generatedAt,
        workspaceState: props.workspaceState,
        premiumDemo: props.premiumDemo,
        improveLoopModel: props.improveLoopModel,
        deploymentDecision: props.deploymentDecision,
      }),
    [
      props.deploymentDecision,
      props.generatedAt,
      props.improveLoopModel,
      props.premiumDemo,
      props.workspaceState,
    ]
  );

  const exportJson = useMemo(
    () => serializeStudioExportPackage(exportPackage),
    [exportPackage]
  );
  const markdownSummary = useMemo(
    () => formatStudioExportMarkdownSummary(exportPackage),
    [exportPackage]
  );
  const exportContent = activeExportView === "json" ? exportJson : markdownSummary;

  const publishPackage = useMemo(
    () =>
      buildStudioPublishPackage({
        generatedAt: props.generatedAt,
        workspaceState: props.workspaceState,
        premiumDemo: props.premiumDemo,
        improveLoopModel: props.improveLoopModel,
        deploymentDecision: props.deploymentDecision,
      }),
    [
      props.deploymentDecision,
      props.generatedAt,
      props.improveLoopModel,
      props.premiumDemo,
      props.workspaceState,
    ]
  );
  const publishJson = useMemo(
    () => serializeStudioPublishPackage(publishPackage),
    [publishPackage]
  );
  const publishMarkdownSummary = useMemo(
    () => formatStudioPublishMarkdownSummary(publishPackage),
    [publishPackage]
  );
  const publishContent =
    activePublishView === "json" ? publishJson : publishMarkdownSummary;

  function handleDownloadCurrentExportView() {
    downloadStudioExportContent({
      content: exportContent,
      fileName: buildDownloadFileName(
        "export",
        activeExportView,
        exportPackage.generatedAt
      ),
      mimeType:
        activeExportView === "json"
          ? "application/json"
          : "text/markdown;charset=utf-8",
    });
  }

  function handleDownloadCurrentPublishView() {
    downloadStudioExportContent({
      content: publishContent,
      fileName: buildDownloadFileName(
        "publish",
        activePublishView,
        publishPackage.generatedAt
      ),
      mimeType:
        activePublishView === "json"
          ? "application/json"
          : "text/markdown;charset=utf-8",
    });
  }

  return (
    <div style={{ marginTop: "1.2rem", display: "grid", gap: "1rem" }}>
      <section
        aria-labelledby="studio-export-heading"
        style={{
          borderRadius: 28,
          border: "1px solid rgba(54, 44, 35, 0.1)",
          background: "rgba(255, 252, 248, 0.96)",
          padding: "1.2rem",
          boxShadow: "0 24px 50px rgba(77, 61, 44, 0.07)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: "1rem",
            alignItems: "flex-start",
            flexWrap: "wrap",
          }}
        >
          <div style={{ maxWidth: 760 }}>
            <div
              style={{
                fontSize: "0.78rem",
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                color: "#7b726a",
                fontWeight: 700,
              }}
            >
              Export handoff package
            </div>
            <h3
              id="studio-export-heading"
              style={{
                margin: "0.45rem 0 0",
                fontSize: "clamp(1.5rem, 2.5vw, 2rem)",
                lineHeight: 1.1,
                fontFamily: "Georgia, 'Times New Roman', serif",
              }}
            >
              Export and handoff package
            </h3>
            <p style={{ margin: "0.75rem 0 0", color: "#4d453d", lineHeight: 1.7 }}>
              Use the export package for founder-demo prep, operator review,
              blocker-aware handoff, and rollout review. It records
              the current Studio draft persona and preview posture while keeping
              preview-runtime truth explicit: mapped personas can influence the
              internal preview lane only, and unmapped personas fall back safely.
            </p>
          </div>
          <div style={{ display: "flex", gap: "0.55rem", flexWrap: "wrap" }}>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                borderRadius: 999,
                padding: "0.3rem 0.64rem",
                background: "rgba(44, 93, 67, 0.12)",
                color: "#214735",
                fontSize: "0.74rem",
                fontWeight: 700,
              }}
            >
              audit-safe
            </span>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                borderRadius: 999,
                padding: "0.3rem 0.64rem",
                background: "rgba(162, 62, 62, 0.12)",
                color: "#812d2d",
                fontSize: "0.74rem",
                fontWeight: 700,
              }}
            >
              no live deploy
            </span>
          </div>
        </div>

        <div
          style={{
            marginTop: "1rem",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
            gap: "0.85rem",
          }}
        >
          {summaryCard(
            "Scenarios",
            String(exportPackage.scenarioResultsSummary.totalScenarios),
            `${exportPackage.scenarioResultsSummary.readyCount} ready / ${exportPackage.scenarioResultsSummary.reviewCount} review / ${exportPackage.scenarioResultsSummary.blockedCount} blocked`
          )}
          {summaryCard(
            "Humanization",
            `${exportPackage.humanizationScoreSummary.totalScore}/${exportPackage.humanizationScoreSummary.maxScore}`,
            `Current status: ${exportPackage.humanizationScoreSummary.status}`
          )}
          {summaryCard(
            "Readiness state",
            exportPackage.deploymentReadinessStatus.stateLabel,
            exportPackage.deploymentReadinessStatus.summary
          )}
        </div>

        <div
          style={{
            marginTop: "1rem",
            display: "flex",
            gap: "0.75rem",
            flexWrap: "wrap",
          }}
        >
          {viewButton(
            "View JSON package",
            "json",
            activeExportView,
            setActiveExportView
          )}
          {viewButton(
            "View Markdown summary",
            "markdown",
            activeExportView,
            setActiveExportView
          )}
          <button
            type="button"
            onClick={handleDownloadCurrentExportView}
            style={{
              borderRadius: 999,
              padding: "0.72rem 1rem",
              border: "1px solid rgba(54, 44, 35, 0.12)",
              background: "#fff9f2",
              color: "#3e372f",
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            {activeExportView === "json"
              ? "Download JSON package"
              : "Download Markdown summary"}
          </button>
        </div>

        <div
          style={{
            marginTop: "1rem",
            borderRadius: 22,
            border: "1px solid rgba(54, 44, 35, 0.08)",
            background:
              "linear-gradient(180deg, rgba(240, 248, 250, 0.9), rgba(248, 251, 252, 0.98))",
            padding: "1rem",
          }}
        >
          <div
            style={{
              fontSize: "0.74rem",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "#5b7884",
              fontWeight: 700,
            }}
          >
            {activeExportView === "json" ? "JSON package" : "Markdown summary"}
          </div>
          <pre
            aria-label="Studio export content"
            style={{
              margin: "0.8rem 0 0",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              color: "#39535f",
              lineHeight: 1.65,
              fontSize: "0.9rem",
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
            }}
          >
            {exportContent}
          </pre>
        </div>
      </section>

      <section
        aria-labelledby="studio-publish-heading"
        style={{
          borderRadius: 28,
          border: "1px solid rgba(41, 84, 103, 0.14)",
          background:
            "linear-gradient(180deg, rgba(240, 248, 250, 0.9), rgba(248, 251, 252, 0.98))",
          padding: "1.2rem",
          boxShadow: "0 24px 50px rgba(41, 84, 103, 0.08)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: "1rem",
            alignItems: "flex-start",
            flexWrap: "wrap",
          }}
        >
          <div style={{ maxWidth: 760 }}>
            <div
              style={{
                fontSize: "0.78rem",
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                color: "#5b7884",
                fontWeight: 700,
              }}
            >
              Internal handoff package
            </div>
            <h3
              id="studio-publish-heading"
              style={{
                margin: "0.45rem 0 0",
                fontSize: "clamp(1.5rem, 2.5vw, 2rem)",
                lineHeight: 1.1,
                fontFamily: "Georgia, 'Times New Roman', serif",
              }}
            >
              Approval and handoff package
            </h3>
            <p style={{ margin: "0.75rem 0 0", color: "#39535f", lineHeight: 1.7 }}>
              Use the handoff package for evidence-backed internal signoff review,
              operator handoff, and rollout review. It captures current
              blocker truth, records internal review posture, and does not activate
              production.
            </p>
          </div>
          <div style={{ display: "flex", gap: "0.55rem", flexWrap: "wrap" }}>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                borderRadius: 999,
                padding: "0.3rem 0.64rem",
                background: "rgba(44, 93, 67, 0.12)",
                color: "#214735",
                fontSize: "0.74rem",
                fontWeight: 700,
              }}
            >
              internal-only
            </span>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                borderRadius: 999,
                padding: "0.3rem 0.64rem",
                background: "rgba(162, 62, 62, 0.12)",
                color: "#812d2d",
                fontSize: "0.74rem",
                fontWeight: 700,
              }}
            >
              blocker-honest
            </span>
          </div>
        </div>

        <div
          style={{
            marginTop: "1rem",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
            gap: "0.85rem",
          }}
        >
          {summaryCard(
            "Approval",
            publishPackage.approval.statusLabel,
            publishPackage.approval.approvalTimestamp || "Sign-off not yet recorded"
          )}
          {summaryCard(
            "Readiness decision",
            publishPackage.readinessDecision.stateLabel,
            publishPackage.readinessDecision.summary
          )}
          {summaryCard(
            "Blockers",
            String(publishPackage.blockerList.length),
            publishPackage.blockerList.length === 0
              ? "No current blockers are attached to this package."
              : "Current blockers remain attached honestly to this package."
          )}
        </div>

        <div
          style={{
            marginTop: "1rem",
            display: "flex",
            gap: "0.75rem",
            flexWrap: "wrap",
          }}
        >
          {viewButton(
            "View handoff JSON",
            "json",
            activePublishView,
            setActivePublishView
          )}
          {viewButton(
            "View handoff Markdown",
            "markdown",
            activePublishView,
            setActivePublishView
          )}
          <button
            type="button"
            aria-label="Download handoff package"
            onClick={handleDownloadCurrentPublishView}
            disabled={!publishPackage.approval.approvalTimestamp}
            style={{
              borderRadius: 999,
              padding: "0.72rem 1rem",
              border: publishPackage.approval.approvalTimestamp
                ? "1px solid rgba(54, 44, 35, 0.12)"
                : "1px solid rgba(181, 141, 34, 0.22)",
              background: publishPackage.approval.approvalTimestamp
                ? "#fff9f2"
                : "rgba(181, 141, 34, 0.12)",
              color: publishPackage.approval.approvalTimestamp ? "#3e372f" : "#6d5210",
              fontWeight: 700,
              cursor: publishPackage.approval.approvalTimestamp
                ? "pointer"
                : "not-allowed",
            }}
          >
            {activePublishView === "json"
              ? "Download handoff JSON package"
              : "Download handoff Markdown summary"}
          </button>
        </div>

        {!publishPackage.approval.approvalTimestamp ? (
          <p style={{ margin: "0.8rem 0 0", color: "#6d5210", lineHeight: 1.65 }}>
            Record internal signoff in the checklist before downloading the handoff
            package. This enables internal review packaging only, not deployment
            approval.
          </p>
        ) : null}

        <div
          style={{
            marginTop: "1rem",
            borderRadius: 22,
            border: "1px solid rgba(54, 44, 35, 0.08)",
            background: "rgba(255, 255, 255, 0.9)",
            padding: "1rem",
          }}
        >
          <div
            style={{
              fontSize: "0.74rem",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "#5b7884",
              fontWeight: 700,
            }}
          >
            {activePublishView === "json"
              ? "Handoff JSON package"
              : "Handoff Markdown summary"}
          </div>
          <pre
            aria-label="Studio handoff content"
            style={{
              margin: "0.8rem 0 0",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              color: "#39535f",
              lineHeight: 1.65,
              fontSize: "0.9rem",
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
            }}
          >
            {publishContent}
          </pre>
        </div>
      </section>
    </div>
  );
}
